import { useEffect, useRef, useState } from 'react';
import {
  BACKFILL_PER_VISIT, MAX_THUMBNAIL_BYTES, THUMBNAIL_MAX_HEIGHT, THUMBNAIL_QUALITY,
  THUMBNAIL_RETRY_QUALITY, THUMBNAIL_WIDTH, backfillCandidates, thumbnailKindOf, type BackfillRow,
} from '../core/thumbnails';
import { putThumbnail, type GetToken } from './api';
import type { PdfEngine } from './pdf';

/**
 * Drawing a material's thumbnail in the browser, and filling in the ones that are missing.
 *
 * The Worker never draws: it checks a JPEG and stores it. So the picture is made here, by the
 * uploader's own browser, from bytes it already holds — the file it just sent, or the preview
 * it fetched with its bearer token:
 *
 * - **A PDF** is parsed by `src/pdf.ts`'s engine, which keeps that file's four rules — bytes
 *   and never a URL, a same-origin worker, `useWasm: false`, and pdf.js behind a dynamic
 *   `import()` so a teacher who never uploads a PDF never downloads it. Page 1 is painted at
 *   320 px wide and the document is destroyed whatever happens.
 * - **A PNG or JPEG** is decoded and drawn smaller, onto white, so a transparent PNG does not
 *   turn black as a JPEG.
 * - **Anything else** has no thumbnail and keeps its icon.
 *
 * Both are encoded with `canvas.toBlob('image/jpeg', 0.8)`, and once more at 0.5 if the first is
 * over the Worker's 256 KB. If that is still too big there is no thumbnail, and that is fine.
 *
 * Everything that touches the DOM comes in through `RenderDeps`, so a test hands in a canvas
 * and a PDF engine and asserts what they were asked to do. jsdom has neither.
 */

/** The subset of a 2D context this file draws with. */
export interface ThumbContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, width: number, height: number): void;
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}

/** The subset of `HTMLCanvasElement` this file uses. */
export interface ThumbCanvas {
  width: number;
  height: number;
  getContext(kind: '2d'): ThumbContext | null;
  toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

export interface DecodedImage {
  readonly width: number;
  readonly height: number;
  readonly source: CanvasImageSource;
  close?(): void;
}

export interface RenderDeps {
  pdf: PdfEngine;
  createCanvas(width: number, height: number): ThumbCanvas;
  decodeImage(blob: Blob): Promise<DecodedImage>;
}

/** What a thumbnail is drawn from. The same two shapes `fetchMaterialPreview` answers with. */
export type ThumbnailSource =
  | { kind: 'pdf'; bytes: ArrayBuffer }
  | { kind: 'image'; blob: Blob };

export interface RenderedThumbnail {
  readonly blob: Blob;
  /** Pages in the PDF; null for a picture. */
  readonly pageCount: number | null;
}

/** The scale that fits a `width × height` thing into a card's thumbnail, never enlarging it. */
const fitScale = (width: number, height: number): number =>
  Math.min(1, THUMBNAIL_WIDTH / width, THUMBNAIL_MAX_HEIGHT / height);

const px = (value: number): number => Math.max(1, Math.round(value));

const toJpeg = (canvas: ThumbCanvas, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => { canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality); });

/** Encode, retry once smaller, or give up. */
async function encode(canvas: ThumbCanvas): Promise<Blob | null> {
  for (const quality of [THUMBNAIL_QUALITY, THUMBNAIL_RETRY_QUALITY]) {
    const blob = await toJpeg(canvas, quality);
    if (blob && blob.size > 0 && blob.size <= MAX_THUMBNAIL_BYTES) return blob;
  }
  return null;
}

/**
 * Draw one thumbnail. Answers null when it cannot be made small enough, and rejects when the
 * source cannot be drawn at all; the caller treats both as "no thumbnail".
 */
export async function renderThumbnail(source: ThumbnailSource, deps: RenderDeps): Promise<RenderedThumbnail | null> {
  if (source.kind === 'pdf') {
    const doc = await deps.pdf.load(source.bytes);
    try {
      const page = await doc.getPage(1);
      const natural = page.getViewport({ scale: 1 });
      // pdf.js may paint a page larger than its natural size; the cap still holds.
      const scale = Math.min(THUMBNAIL_WIDTH / natural.width, THUMBNAIL_MAX_HEIGHT / natural.height);
      const viewport = page.getViewport({ scale });
      const canvas = deps.createCanvas(px(viewport.width), px(viewport.height));
      // pdf.js fills the page white itself, so there is no ground to paint first.
      await page.render({ canvas: canvas as unknown as HTMLCanvasElement, viewport }).promise;
      const blob = await encode(canvas);
      return blob ? { blob, pageCount: doc.numPages } : null;
    } finally {
      await doc.destroy().catch(() => undefined);
    }
  }

  const image = await deps.decodeImage(source.blob);
  try {
    const scale = fitScale(image.width, image.height);
    const width = px(image.width * scale);
    const height = px(image.height * scale);
    const canvas = deps.createCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image.source, 0, 0, width, height);
    const blob = await encode(canvas);
    return blob ? { blob, pageCount: null } : null;
  } finally {
    image.close?.();
  }
}

/** A file just uploaded, as something to draw from — or null for a type that gets no picture. */
export async function sourceFromFile(contentType: string, file: Blob): Promise<ThumbnailSource | null> {
  const kind = thumbnailKindOf(contentType);
  if (kind === 'pdf') return { kind, bytes: await file.arrayBuffer() };
  if (kind === 'image') return { kind, blob: file };
  return null;
}

export interface ThumbnailCtx extends RenderDeps {
  /** `putThumbnail`, bound to a token. True when the Worker stored it. */
  upload(materialId: string, blob: Blob, pageCount: number | null): Promise<boolean>;
}

/**
 * Draw and store one thumbnail. Null when there was nothing to store; a rejection when the
 * Worker refused it, so the upload panel's `thumbnail` step can count it failed. Either way
 * the material itself is untouched.
 */
export async function makeThumbnail(
  ctx: ThumbnailCtx,
  materialId: string,
  source: ThumbnailSource,
): Promise<{ pageCount: number | null } | null> {
  const drawn = await renderThumbnail(source, ctx);
  if (!drawn) return null;
  const stored = await ctx.upload(materialId, drawn.blob, drawn.pageCount);
  if (!stored) throw new Error('thumbnail_refused');
  return { pageCount: drawn.pageCount };
}

/** The real canvas and image decoder. Built only in the browser. */
export const domRenderDeps = (pdf: PdfEngine): RenderDeps => ({
  pdf,
  createCanvas: (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas as unknown as ThumbCanvas;
  },
  decodeImage: async (blob) => {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(blob);
      return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
    }
    // Older Safari. An object URL for a picture the viewer already holds, revoked at once.
    const url = URL.createObjectURL(blob);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      return { width: image.naturalWidth, height: image.naturalHeight, source: image };
    } finally {
      URL.revokeObjectURL(url);
    }
  },
});

/** The real renderer, storing through `putThumbnail` with the viewer's token. */
export const thumbnailCtx = (getToken: GetToken, pdf: PdfEngine): ThumbnailCtx => ({
  ...domRenderDeps(pdf),
  upload: (materialId, blob, pageCount) => putThumbnail(getToken, materialId, blob, pageCount),
});

// --- backfill --------------------------------------------------------------------------------

export interface BackfillOptions<T extends BackfillRow> {
  /** A visit is one group; changing it starts the count again. */
  groupId: string;
  materials: readonly T[];
  /** Only once the list has rendered. */
  ready: boolean;
  viewerUserId: string | null;
  /** Materials whose thumbnail the upload panel is making right now. */
  busy: ReadonlySet<string>;
  /** The material's bytes, through the preview route. Null when they could not be had. */
  fetchSource: (material: T) => Promise<ThumbnailSource | null>;
  make: (materialId: string, source: ThumbnailSource) => Promise<{ pageCount: number | null } | null>;
  /** Told when one was stored, so the card can show it without a reload. */
  onMade: (materialId: string, pageCount: number | null) => void;
  limit?: number;
  /** Absent, the backfill does nothing — a component test that does not ask for one gets none. */
  enabled?: boolean;
}

/**
 * Project 180's `fillThumbnail`, for Collab: when the room lists one of the viewer's own PDFs
 * or pictures with no thumbnail, draw one in the background.
 *
 * One at a time, at most `BACKFILL_PER_VISIT` per visit, starting after the list has painted.
 * It never blocks the room and never says anything: a failure is a `console.debug` and the
 * card keeps its icon. Each material is tried once per visit, success or not.
 */
export function useThumbnailBackfill<T extends BackfillRow>({
  groupId, materials, ready, viewerUserId, busy, fetchSource, make, onMade,
  limit = BACKFILL_PER_VISIT, enabled = true,
}: BackfillOptions<T>): void {
  const tried = useRef<{ groupId: string; ids: Set<string> }>({ groupId, ids: new Set() });
  const running = useRef(false);
  const alive = useRef(true);
  const [tick, setTick] = useState(0);
  const latest = useRef({ fetchSource, make, onMade });
  useEffect(() => { latest.current = { fetchSource, make, onMade }; });

  // Unmounting stops the run at its next step. A change of `materials` must not: storing one
  // thumbnail changes the list, and that is not a reason to abandon the rest.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  if (tried.current.groupId !== groupId) tried.current = { groupId, ids: new Set() };

  useEffect(() => {
    if (!enabled || !ready || running.current) return;
    const visit = tried.current;
    const skip = new Set([...visit.ids, ...busy]);
    const queue = backfillCandidates(materials, viewerUserId, skip, limit - visit.ids.size);
    if (queue.length === 0) return;

    const current = () => alive.current && tried.current === visit;
    running.current = true;
    // After the list has painted: a macrotask, not the effect's own turn.
    setTimeout(() => {
      void (async () => {
        for (const material of queue) {
          if (!current()) break;
          visit.ids.add(material.id);
          try {
            const source = await latest.current.fetchSource(material);
            if (!source || !current()) continue;
            const made = await latest.current.make(material.id, source);
            if (made && current()) latest.current.onMade(material.id, made.pageCount);
          } catch (error) {
            console.debug('[collab] thumbnail backfill skipped', error);
          }
        }
        running.current = false;
        // Anything that became a candidate while this ran is looked at now.
        if (alive.current) setTick((n) => n + 1);
      })();
    }, 0);
  }, [enabled, ready, materials, viewerUserId, busy, limit, groupId, tick]);
}
